import { fetch, addTask } from 'domain-task';
import { Action, Reducer, ActionCreator } from 'redux';
import { AppThunkAction } from './';
var Config: any = require('../Config.json');

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface DeploymentSearchState {
    isLoading: boolean;
    searchFilters: server.DeploymentSearchFilters;
    deploymentStatus: server.USP_DeploymentStatusUI_Model[];
    columnDefs: any[];
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

interface RequestDeploymentSearchAction {
    type: 'REQUEST_DEPLOYMENT_SEARCH';
    searchFilters: server.DeploymentSearchFilters;
    colDefs: any[];
}

interface ReceiveDeploymentSearchAction {
    type: 'RECEIVE_DEPLOYMENT_SEARCH';
    searchFilters: server.DeploymentSearchFilters;
    deploymentStatusData: server.USP_DeploymentStatusUI_Model[];
    colDefs: any[];
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction = RequestDeploymentSearchAction | ReceiveDeploymentSearchAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    requestDeploymentSearch: (searchFilters: server.DeploymentSearchFilters, reeferCols: any[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        let fetchTask = fetch(Config.ApiBaseUrl + '/api/deploymentstatus/DeploymentStatusDataSP', { method: 'POST', body: JSON.stringify(searchFilters), headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } })
            .then(response => response.json() as Promise<server.USP_DeploymentStatusUI_Model[]>)
            .then(data => {
                dispatch({ type: 'RECEIVE_DEPLOYMENT_SEARCH', searchFilters: searchFilters, deploymentStatusData: data, colDefs: reeferCols });
            });

        addTask(fetchTask); // Ensure server-side prerendering waits for this to complete
        dispatch({ type: 'REQUEST_DEPLOYMENT_SEARCH', searchFilters: searchFilters, colDefs: reeferCols });
    }
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
const emptySearchFilter = {
    CreatedBy: null, DeploymentName: null, DeploymentTypeId: null, FileName: null, FromDate: null, ModifiedBy: null,
    DeploymentStatusId: null, ToDate: null, Topresult: null, UserID: null, CustomerId: null, CustomerTypeCd: null
}
const unloadedState: DeploymentSearchState = { isLoading: true, searchFilters: emptySearchFilter, deploymentStatus: [], columnDefs: [] };

export const reducer: Reducer<DeploymentSearchState> = (state: DeploymentSearchState, incomingAction: Action) => {
    const action = incomingAction as KnownAction;
    switch (action.type) {
        case 'REQUEST_DEPLOYMENT_SEARCH':
            return {
                searchFilters: action.searchFilters,
                deploymentStatus: state.deploymentStatus,
                isLoading: true,
                columnDefs: state.columnDefs
            };
        case 'RECEIVE_DEPLOYMENT_SEARCH':
            // Only accept the incoming data if it matches the most recent request. This ensures we correctly
            // handle out-of-order responses.
            if (action.searchFilters === state.searchFilters) {
                return {
                    searchFilters: action.searchFilters,
                    deploymentStatus: action.deploymentStatusData,
                    isLoading: false,
                    columnDefs: action.colDefs
                };
            }
            break;
        default:
            // The following line guarantees that every action in the KnownAction union has been covered by a case above
            const exhaustiveCheck: never = action;
    }

    return state || unloadedState;
};
